import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { Budget } from '@/types/financial';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { useEncryption } from '../EncryptionContext';
import { loadBudgetsFromSupabase } from '@/lib/supabase-operations';
import { budgetsReducer, initialState } from './reducer';
import { useBudgetSelectors } from './selectors';
import { createBudget, updateBudget as updateBudgetOp, deleteBudget as deleteBudgetOp } from './operations';
import { logger } from '@/utils/logger';

interface BudgetsContextType {
  budgets: Budget[];
  addBudget: (budget: Omit<Budget, 'id' | 'createdAt'>) => Promise<Budget>;
  updateBudget: (id: string, updates: Partial<Budget>) => Promise<void>;
  deleteBudget: (id: string) => Promise<void>;
  getBudgetById: (id: string) => Budget | undefined;
  isLoading: boolean;
  error: string | null;
}

const BudgetsContext = createContext<BudgetsContextType | undefined>(undefined);

export const BudgetsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(budgetsReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();
  
  const selectors = useBudgetSelectors(state.budgets);

  // Load budgets from Supabase
  useEffect(() => {
    if (!user) {
      dispatch({ type: 'CLEAR_BUDGETS' });
      return;
    }

    if (isCheckingEncryption) {
      logger.debug('[Budgets] Waiting for encryption check...');
      return;
    }
    
    if (!isEncryptionReady) {
      logger.warn('[Budgets] Encryption not ready, skipping load');
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    const loadBudgets = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      try {
        logger.info('[Budgets] Loading budgets from Supabase');
        const supabaseBudgets = await loadBudgetsFromSupabase();
        dispatch({ type: 'SET_BUDGETS', payload: supabaseBudgets });
        logger.info('[Budgets] Loaded budgets', { count: supabaseBudgets.length });
      } catch (error) {
        logger.error('[Budgets] Error loading budgets', { error });
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar orçamentos' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadBudgets();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload when profile changes
  useEffect(() => {
    if (user && activeProfile && isEncryptionReady && !isCheckingEncryption) {
      logger.info('[Budgets] Profile changed, reloading budgets', { profileId: activeProfile.id });
      
      const loadBudgets = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
          const supabaseBudgets = await loadBudgetsFromSupabase();
          dispatch({ type: 'SET_BUDGETS', payload: supabaseBudgets });
        } catch (error) {
          logger.error('[Budgets] Error reloading budgets', { error });
          dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar orçamentos' });
        }
      };
      
      loadBudgets();
    }
  }, [activeProfile?.id, user, isEncryptionReady, isCheckingEncryption]);

  const addBudget = useCallback(async (
    budgetData: Omit<Budget, 'id' | 'createdAt'>
  ): Promise<Budget> => {
    try {
      const newBudget = await createBudget(budgetData);
      dispatch({ type: 'ADD_BUDGET', payload: newBudget });
      dispatch({ type: 'SET_ERROR', payload: null });
      return newBudget;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar orçamento';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  const updateBudget = useCallback(async (id: string, updates: Partial<Budget>) => {
    try {
      const currentBudget = state.budgets.find(b => b.id === id);
      if (!currentBudget) throw new Error('Budget not found');

      const updatedBudget = await updateBudgetOp(id, updates, currentBudget);
      dispatch({ type: 'UPDATE_BUDGET', payload: { id, updates: updatedBudget } });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar orçamento';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state.budgets]);

  const deleteBudget = useCallback(async (id: string) => {
    try {
      await deleteBudgetOp(id);
      dispatch({ type: 'DELETE_BUDGET', payload: id });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao excluir orçamento';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  const value = useMemo<BudgetsContextType>(() => ({
    budgets: state.budgets,
    addBudget,
    updateBudget,
    deleteBudget,
    getBudgetById: selectors.getById,
    isLoading: state.isLoading,
    error: state.error,
  }), [state, addBudget, updateBudget, deleteBudget, selectors]);

  return (
    <BudgetsContext.Provider value={value}>
      {children}
    </BudgetsContext.Provider>
  );
};

export const useBudgets = () => {
  const context = useContext(BudgetsContext);
  if (!context) {
    throw new Error('useBudgets must be used within a BudgetsProvider');
  }
  return context;
};
